import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  assistants: [],
  loading: false,
  isSuccess: false,
  errors: []
};

// Toutes ces routes sont réservées SUPER_ADMIN côté backend (user.controller.ts) —
// un assistant ne peut jamais gérer d'autres comptes assistants.

export const fetchAssistants = createAsyncThunk(
  'assistants/fetchAssistants',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/user/assistant');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const createAssistant = createAsyncThunk(
  'assistants/createAssistant',
  async (assistantData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/user/assistant', assistantData);
      dispatch(fetchAssistants());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAssistantPermissions = createAsyncThunk(
  'assistants/updateAssistantPermissions',
  async ({ id, permissions }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/assistant/${id}/permissions`, { permissions });
      dispatch(fetchAssistants());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAssistant = createAsyncThunk(
  'assistants/deleteAssistant',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/user/assistant/${id}`);
      dispatch(fetchAssistants());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const assistantsSlice = createSlice({
  name: 'assistants',
  initialState,
  reducers: {
    clearAssistantErrors: (state) => {
      state.errors = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssistants.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssistants.fulfilled, (state, { payload }) => {
        state.assistants = payload;
        state.loading = false;
      })
      .addCase(fetchAssistants.rejected, (state, { payload }) => {
        // Ne jamais laisser une ancienne liste affichée après un échec (ex: 403 parce
        // que la session en cours n'est plus super admin) — mieux vaut une liste vide
        // et visible qu'une liste périmée qui laisse croire que l'accès est toujours bon.
        state.assistants = [];
        state.errors = payload?.message ? [payload.message] : ['error'];
        state.loading = false;
      })
      .addCase(createAssistant.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
      })
      .addCase(createAssistant.fulfilled, (state) => {
        state.loading = false;
        state.isSuccess = true;
      })
      .addCase(createAssistant.rejected, (state, { payload }) => {
        state.errors = payload?.message ? [payload.message] : ['error'];
        state.loading = false;
        state.isSuccess = false;
      })
      .addCase(updateAssistantPermissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAssistantPermissions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAssistantPermissions.rejected, (state, { payload }) => {
        state.errors = payload?.message ? [payload.message] : ['error'];
        state.loading = false;
      })
      .addCase(deleteAssistant.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAssistant.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteAssistant.rejected, (state, { payload }) => {
        state.errors = payload?.message ? [payload.message] : ['error'];
        state.loading = false;
      });
  }
});

export const { clearAssistantErrors } = assistantsSlice.actions;
export default assistantsSlice.reducer;
